import { useContext, useMemo, type FC } from 'react';
import InlineOptions from '../../inline-options';
import { LatestRealizedVolsContext } from '../../../../providers/latest-realized-vols-provider';
import TokenFilter from './token-filter';
import Utils from '../../../../utils/immersive_utils';
import './latest-realized-vols-styles.scss';

import BinanceIcon from '../../../../assets/icons/binance-icon.svg';
import DeribitIcon from '../../../../assets/icons/deribit-icon.svg';
import OKXIcon from '../../../../assets/icons/okx-icon.svg';
import OTCIcon from '../../../../assets/icons/otc-icon.svg';
import MexcIcon from '../../../../assets/icons/mexc-icon.svg';
import TableGrid from '../../../table-grid';
import TimestampInfo from '../../timestamp-info';

const venueOptions = [
  { label: 'Deribit', value: 'DERIBIT' },
  { label: 'Binance', value: 'BINANCE' },
  { label: 'Mexc', value: 'MEXC' },
];

const iconMap: { [key: string]: any } = {
  deribit: DeribitIcon,
  okx: OKXIcon,
  binance: BinanceIcon,
  otc: OTCIcon,
  mexc: MexcIcon,
};

const LatestRealizedVolsContent: FC = () => {
  const {
    data,
    isLoading,
    tokenListLoading,
    selectedVenues,
    setSelectedVenues,
    tokenList,
    setTokenList,
    tokenPairs,
    setTokenPairs,
    columnOrder,
    timestamp,
  } = useContext(LatestRealizedVolsContext);
  const renderFilters = () => {
    const handleSelectTokenPairs = (selectedList: string[]) => {
      setTokenPairs((prev) => {
        return prev.map((item) => ({
          ...item,
          isVisible: selectedList.includes(item.value),
        }));
      });
    };

    const selectedOptions =
      tokenPairs?.filter((pair) => pair.isVisible).map((pair) => pair.value) || [];

    return (
      <div className="lastest-realized-filters">
        <InlineOptions
          options={tokenPairs}
          selectedOption={selectedOptions}
          onOptionChange={(list: any) => handleSelectTokenPairs(list as string[])}
          isMultipleSelect
          isFilter
        />
      </div>
    );
  };

  const formatCurrency = (value: number) =>
    isNaN(value) || value === null || value === undefined
      ? '-'
      : Utils.valueToCurrency(value, value < 1 ? 4 : 2);

  const formatPercent = (value: number, decimals: number = 2) =>
    isNaN(value) || value === null || value === undefined
      ? '-'
      : `${(value * 100).toFixed(decimals)}%`;

  const formattedData = useMemo(() => {
    if (!data || !tokenPairs.length || !data.length) return [];
    const visibleSymbols = tokenPairs.filter((pair) => pair.isVisible).map((pair) => pair.value);

    return data
      .filter((row) => visibleSymbols.includes(row.symbol))
      .map((row) => ({
        symbol: row.symbol,
        exchange: row.exchange,
        price: formatCurrency(row.price),
        change_24: formatPercent(row.change_24),
        low_24: formatCurrency(row.low_24),
        high_24: formatCurrency(row.high_24),
        vol_3d: formatPercent(row.vol_3d),
        vol_1w: formatPercent(row.vol_1w),
        vol_2w: formatPercent(row.vol_2w),
        vol_1m: formatPercent(row.vol_1m),
        vol_3m: formatPercent(row.vol_3m),
        vol_6m: formatPercent(row.vol_6m),
      }));
  }, [data, tokenPairs]);

  const DEFAULT_COLUMNS = [
    {
      field: 'symbol',
      headerName: 'Token Pair',
      minWidth: 120,
    },
    {
      field: 'exchange',
      headerName: 'Exchange',
      minWidth: 120,
      cellRenderer: (params: any) => {
        const value = params.value;
        return (
          <div className="exchange-cell-render">
            {iconMap[value.toLowerCase()] ? (
              <img src={iconMap[value.toLowerCase()]} className="exchange-icon" alt={value} />
            ) : (
              <div className="no-icon" />
            )}
            <span> {value}</span>
          </div>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Price',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'change_24',
      headerName: '24h Ch.',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'low_24',
      headerName: '24h Low',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'high_24',
      headerName: '24h High',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'vol_3d',
      headerName: '3D/1h RV',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'vol_1w',
      headerName: '1W/1h RV',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'vol_2w',
      headerName: '2W/1h RV',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'vol_1m',
      headerName: '1M/1h RV',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'vol_3m',
      headerName: '3M/1h RV',
      minWidth: 120,
      align: 'right',
    },
    {
      field: 'vol_6m',
      headerName: '6M/1h RV',
      minWidth: 120,
      align: 'right',
    },
  ];

  const getColumns = useMemo(() => {
    if (Array.isArray(columnOrder) && columnOrder.length > 0) {
      const columnMap = DEFAULT_COLUMNS.reduce(
        (acc, col) => {
          acc[col.field] = col;
          return acc;
        },
        {} as Record<string, any>,
      );
      return columnOrder
        .map((name) => columnMap[name])
        .filter(Boolean)
        .concat(DEFAULT_COLUMNS.filter((col) => !columnOrder.includes(col.field)));
    }
    return DEFAULT_COLUMNS;
  }, [columnOrder]);

  return (
    <div className="latest-realized-vols-content">
      <div className="latest-realized-header">
        <div className="options-header">
          <InlineOptions
            options={venueOptions}
            selectedOption={selectedVenues}
            onOptionChange={(options: any) => setSelectedVenues(options)}
            isMultipleSelect
          />
        </div>
        <TokenFilter tokens={tokenList} handleUpdate={setTokenList} isLoading={tokenListLoading} />
      </div>
      {renderFilters()}
      <div className="latest-realized-timestamp">
        <TimestampInfo timestamp={new Date(timestamp).getTime()} />
      </div>
      <div className="latest-realized-body">
        <TableGrid columns={getColumns} rowData={formattedData} isLoading={isLoading} />
      </div>
    </div>
  );
};
export default LatestRealizedVolsContent;
